import { Router, ActivatedRoute, NavigationExtras, RouterEvent, NavigationEnd, NavigationError } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { filter, map, Observable, startWith } from 'rxjs';
import { NotificationActionApiModel } from '@app/api/api-models/notifications/NotificationActionApiModel';
import { ProgramRole } from '../enums';
import { TokenStorageService } from '../services/token-storage.service';
import { CustomUtils } from '../utils/custom-utils';
import { EventsService } from './events.service';
import { SystemEvent } from '../enums/system-event.enum';
import { Location } from '@angular/common';
import { getProgramTypeStringUrl } from '../utils/get-enum/get-program-type';
import { TraineeToOtherRolesPipe } from '../pipes/trainee-to-other-roles.pipe';
import { SiteRole } from '../enums/site-role.enum';
import { APP_CONSTANTS } from '@shared/constants';
import { LogoutMode } from '@app/auth/enums/logout-mode.enum';
import { EidAuthService } from '@app/auth/services/eid-auth.service';
import { CookieService } from 'ngx-cookie-service';
import { UrlSecurityService } from './url-security.service';

@Injectable()
export class RouteHelperService {
  public dashboardBackProgramRoleId: string;

  public get stateName(): string {
    return this._stateName;
  }

  private _stateName = '';
  private lastActivatedRoute: ActivatedRoute;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly tokenStorage: TokenStorageService,
    private readonly eventsService: EventsService,
    private readonly titleService: Title,
    private readonly traineeToOtherRolesPipe: TraineeToOtherRolesPipe,
    private readonly location: Location,
    private readonly eidAuthService: EidAuthService,
    private readonly cookiesService: CookieService,
    private readonly urlSecurityService: UrlSecurityService
  ) {
    this.lastActivatedRoute = route;
    router.events.subscribe(this.scanForStateName.bind(this));
  }

  private scanForStateName(event: RouterEvent): void {
    if (event instanceof NavigationEnd) {
      [this.lastActivatedRoute, this._stateName] = this.findLastActivatedRouteAndTitle(this.route);
      const title = this.getTitlePrefix() + this._stateName;
      this.titleService.setTitle(title);
    }

    if (event instanceof NavigationError) {
      location.reload();
    }
  }

  public setCustomTitle(titlePhrases: string[]) {
    let title = titlePhrases.join(' ' + APP_CONSTANTS.TITLE_SEPARATOR + ' ');
    title = this.getTitlePrefix() + title;
    this.titleService.setTitle(title);
  }

  public navigateToParent(goToGrandParent = false): void {
    if (goToGrandParent) {
      this.router.navigate(['../../'], { relativeTo: this.lastActivatedRoute });
      this.eventsService.emit(SystemEvent.RouteNavigatedBack);
      return;
    }

    this.router.navigate(['../'], { relativeTo: this.lastActivatedRoute });
    this.eventsService.emit(SystemEvent.RouteNavigatedBack);
  }

  public reload(url: string = null, extras?: NavigationExtras): any {
    return this.router
      .navigateByUrl('/blank')
      .then(() => this.router.navigateByUrl(url ? url : this.router.url, extras));
  }

  public redirectByUrl(url: string): Promise<boolean> {
    if (url.startsWith('#')) {
      url = url.substr(1);
    }

    return this.router.navigate([url], { queryParamsHandling: 'preserve' });
  }

  public navigateByUrl(url: string): Promise<boolean> {
    return this.router.navigateByUrl(url);
  }

  public getCurrentUrl(includeQueryParameters = true): string {
    return includeQueryParameters ? this.router.url : this.router.url.split('?')[0];
  }

  public getRouteParam(param: string): any {
    return this.getParam(this.route, param);
  }

  public getParam(route: ActivatedRoute, param: string): any {
    const value = route.snapshot.params[param];

    if (!value) {
      return this.scanRoute(route.root, param);
    }

    return value;
  }

  public getData<T>(key: string): T {
    return this.scanForData(this.lastActivatedRoute, key);
  }

  public getData$<T>(key: string): Observable<T> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.findLastActivatedRouteAndTitle(this.route)),
      startWith(this.lastActivatedRoute),
      map((result) => this.scanForData(result[0], key))
    );
  }

  private getTitlePrefix(): string {
    let titlePrefix = APP_CONSTANTS.APP_NAME + ' - ';
    if (this.tokenStorage?.userInfo?.CurrentProgramName) {
      titlePrefix += this.tokenStorage.userInfo.CurrentProgramName;
      titlePrefix += APP_CONSTANTS.TITLE_SEPARATOR;
    }
    return titlePrefix;
  }

  private scanForData<T>(route: ActivatedRoute, key: string): T {
    return route === null ? null : route.snapshot.data[key] ?? this.scanForData(route.parent, key);
  }

  private scanRoute(route: ActivatedRoute, param: string): any {
    const value = route.snapshot.params[param];

    if (!value && route.firstChild) {
      return this.scanRoute(route.firstChild, param);
    }

    return value;
  }

  public getQueryParam(param: string): any {
    return this.route.snapshot.queryParams[param];
  }

  public pendingTasksReport(): void {
    this.router.navigateByUrl('/reports/pending-tasks');
  }

  public traineeSuccessReport(): void {
    this.router.navigateByUrl('/reports/trainee-success');
  }

  public ppTraineeSuccessReport(): void {
    this.router.navigateByUrl('/pp-reports/employee-success');
  }

  public peIncidentsReport(): void {
    this.router.navigateByUrl('/pe-reports/incidents');
  }

  public ppSupervisorsReport(): void {
    this.router.navigateByUrl('/pp-reports/supervisors');
  }

  public peSupervisorsReport(): void {
    this.router.navigateByUrl('/pe-reports/supervisors');
  }

  public ppPcReport(): void {
    this.router.navigateByUrl('/pp-reports/performance-categories');
  }

  public pePcReport(): void {
    this.router.navigateByUrl('/pe-reports/performance-categories');
  }

  public pcReport(): void {
    this.router.navigateByUrl('/reports/performance-categories');
  }

  public callTypesReport(): void {
    this.router.navigateByUrl('/reports/call-types');
  }

  public skillsReport(): void {
    this.router.navigateByUrl('/reports/skills');
  }

  public trainersReports(): void {
    this.router.navigateByUrl('/reports/trainers');
  }

  public trendingReports(): void {
    this.router.navigateByUrl('/reports/trends');
  }

  public remedialActivitiesReport(): void {
    this.router.navigateByUrl('/reports/remedial-activities');
  }

  public login(includeRedirectUrl = false): void {
    const currentPath = this.location.path(true);

    const redirectUrl =
      includeRedirectUrl && currentPath && currentPath !== '/app/login'
        ? this.getQueryParam('redirectURL') ?? currentPath
        : null;

    if (this.checkIfCommonLogIn()) {
      sessionStorage.setItem('redirectURL', redirectUrl ?? '');
      this.eidAuthService.redirectToEidLogin();
    } else {
      this.router.navigate(['/app/login'], { queryParams: { redirectURL: redirectUrl } });
    }
  }

  public checkIfCommonLogIn(): boolean {
    const path = this.location.path(true).toLowerCase();
    if (path.includes('commonlogin=false')) {
      return false;
    }
    return this.cookiesService.get('eidSession') === 'true' || path.includes('commonlogin=true');
  }

  public activeDirectoryLogin(link: string): void {
    this.router.navigateByUrl(`/app/login/ad/${link}`);
  }

  public logout(): void {
    this.router.navigate(['/app/logout'], { queryParams: { mode: LogoutMode.Manual } });
  }

  public changeCurrent(siteId: number, programId: number | null = null): void {
    this.router.navigateByUrl(`/app/program-change/${siteId}/${programId === null ? '' : programId}`);
  }

  public addUser(
    programRole: ProgramRole | null = null,
    isSystemAdministrator = false,
    trainingCoursesRole: SiteRole | null = null
  ): void {
    this.router.navigate(['/app/user/add'], {
      queryParams: {
        programRole,
        isSystemAdministrator,
        trainingCoursesRole
      }
    });
  }

  public goToUser(id: string): void {
    this.router.navigateByUrl(`/app/user/${id}`);
  }

  public getUserProfileUrl(id: string): string {
    return `/app/user/${id}/account-settings`;
  }

  public editUser(id: string): void {
    this.router.navigateByUrl(`/app/user/${id}/edit`);
  }

  public recover(): void {
    this.router.navigateByUrl('/app/recover');
  }

  public performNotificationAction(id: number, action: NotificationActionApiModel): void {
    this.router.navigate(['/app/notification-action'], {
      queryParams: {
        notificationId: id,
        notificationAction: JSON.stringify(action)
      }
    });
  }

  public superUsers(): void {
    this.router.navigateByUrl('/system-administration/super-users');
  }

  public superUsersActive(): boolean {
    return this.router.url === '/system-administration/super-users';
  }

  public backgroundServices(): void {
    this.router.navigateByUrl('/system-administration/background-services');
  }

  public backgroundServicesActive(): boolean {
    return this.router.url === '/system-administration/background-services';
  }

  public aiDashboard(): void {
    this.router.navigateByUrl('/system-administration/ai/dashboard');
  }

  public aiDashboardActive(): boolean {
    return this.router.url.includes('/system-administration/ai/dashboard');
  }

  public aiPrompts(): void {
    this.router.navigateByUrl('/system-administration/ai/prompts');
  }

  public aiPromptsActive(): boolean {
    return this.router.url.includes('/system-administration/ai/prompts');
  }

  public templates(): void {
    this.router.navigateByUrl('/system-administration/templates');
  }

  public templatesActive(): boolean {
    return this.router.url === '/system-administration/templates';
  }

  public agencyInformation(): void {
    this.router.navigateByUrl('/site-administration/agency-information');
  }

  public agencyInformationActive(): boolean {
    return this.router.url === '/site-administration/agency-information';
  }

  public auditLog(): void {
    this.router.navigateByUrl('/site-administration/audit-log');
  }

  public auditLogActive(): boolean {
    return this.router.url === '/site-administration/audit-log';
  }

  public shifts(): void {
    this.router.navigateByUrl('/site-administration/site-lists/shifts');
  }

  public siteSkills(): void {
    this.router.navigateByUrl('/site-administration/site-lists/skills');
  }

  public callTypes(): void {
    this.router.navigateByUrl('/site-administration/site-lists/call-types');
  }

  public activityTypes(): void {
    this.router.navigateByUrl('/site-administration/site-lists/activity-types');
  }

  public jobTitles(): void {
    this.router.navigateByUrl('/site-administration/site-lists/job-titles');
  }

  public locations(): void {
    this.router.navigateByUrl('/site-administration/site-lists/locations');
  }

  public programExitTypes(): void {
    this.router.navigateByUrl('/site-administration/site-lists/program-exit-types');
  }

  public improvementPlanReasons(): void {
    this.router.navigateByUrl('/site-administration/site-lists/improvement-plan-reasons');
  }

  public improvementActivities(): void {
    this.router.navigateByUrl('/site-administration/site-lists/improvement-activities');
  }

  public improvementPlanConsequences(): void {
    this.router.navigateByUrl('/site-administration/site-lists/improvement-plan-consequences');
  }

  public improvementPlanResolutions(): void {
    this.router.navigateByUrl('/site-administration/site-lists/improvement-plan-resolutions');
  }

  public groups(): void {
    this.router.navigateByUrl('/site-administration/site-lists/groups');
  }

  public quizzes(): void {
    this.router.navigateByUrl('/site-administration/site-lists/quizzes');
  }

  public quiz(id: number): void {
    this.router.navigateByUrl(`/site-administration/site-lists/quizzes/${id}`);
  }

  public programQuizzes(): void {
    this.router.navigateByUrl('/program-setup/quizzes');
  }

  public programQuiz(id: number): void {
    this.router.navigateByUrl(`/program-setup/quizzes/${id}`);
  }

  public programQuizzesActive(): boolean {
    return this.router.url.includes('/program-setup/quizzes');
  }

  public alerts(): void {
    this.router.navigateByUrl('/program-setup/alerts');
  }

  public alertsActive(): boolean {
    return this.router.url === '/program-setup/alerts';
  }

  public tasks(): void {
    this.router.navigateByUrl('/program-setup/tasks');
  }

  public tasksActive(): boolean {
    return this.router.url.includes('/program-setup/tasks');
  }

  public skills(): void {
    this.router.navigateByUrl('/program-setup/skills');
  }

  public skillsActive(): boolean {
    return this.router.url === '/program-setup/skills';
  }

  public performanceCategories(): void {
    this.router.navigateByUrl('/program-setup/performances');
  }

  public performanceActive(): boolean {
    return this.router.url.includes('/program-setup/performances');
  }

  public phases(): void {
    this.router.navigateByUrl('/program-setup/phases');
  }

  public phasesActive(): boolean {
    return this.router.url === '/program-setup/phases';
  }

  public forms(): void {
    this.router.navigateByUrl('/program-setup/ft-forms');
  }

  public formEdit(formId: number): void {
    this.router.navigateByUrl(this.getFormEditUrl(formId));
  }

  public getFormEditUrl(formId: number): string {
    return `/program-setup/ft-forms/${formId}`;
  }

  public formsActive(): boolean {
    return this.router.url.includes('/program-setup/ft-forms');
  }

  public peForms(): void {
    this.router.navigateByUrl('/program-setup/pe-forms');
  }

  public peFormEdit(formId: number): void {
    this.router.navigateByUrl(`/program-setup/pe-forms/${formId}`);
  }

  public peFormsActive(): boolean {
    return this.router.url.includes('/program-setup/pe-forms');
  }

  public ppForms(): void {
    this.router.navigateByUrl('/program-setup/pp-forms');
  }

  public ppFormEdit(formId: number): void {
    this.router.navigateByUrl(`/program-setup/pp-forms/${formId}`);
  }

  public ppFormsActive(): boolean {
    return this.router.url.includes('/program-setup/pp-forms');
  }

  public incidentTypes(): void {
    this.router.navigateByUrl('/program-setup/incident-types');
  }

  public incidentTypesActive(): boolean {
    return this.router.url === '/program-setup/incident-types';
  }

  public correctiveActions(): void {
    this.router.navigateByUrl('/program-setup/corrective-actions');
  }

  public correctiveActionsActive(): boolean {
    return this.router.url === '/program-setup/corrective-actions';
  }

  public search(): void {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);

    this.router.navigateByUrl(`/${prefix}/search`);
  }

  public searchActive(): boolean {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);
    return this.router.url.includes(`/${prefix}/search`);
  }

  public resources(): void {
    this.router.navigateByUrl('/app/resources');
  }

  public resourcesActive(): boolean {
    return this.router.url.includes('/app/resources');
  }

  public mtReports(): void {
    this.router.navigateByUrl('/mt-reports');
  }

  public mtReportsActive(): boolean {
    return this.router.url.includes('/mt-reports');
  }
  public ppReports(): void {
    this.router.navigateByUrl('/pp-reports');
  }

  public ppReportsActive(): boolean {
    return this.router.url.includes('/pp-reports');
  }

  public peReports(): void {
    this.router.navigateByUrl('/pe-reports');
  }

  public peReportsActive(): boolean {
    return this.router.url.includes('/pe-reports');
  }

  public reports(): void {
    this.router.navigateByUrl('/reports');
  }

  public reportsTrainingCourses(): void {
    this.router.navigateByUrl('/training-courses/training-reports');
  }

  public reportsActive(): boolean {
    return this.router.url.includes('/reports');
  }

  public programUsersList(reload = false, rolePostfix = ''): void {
    if (!rolePostfix) {
      rolePostfix = this.traineeToOtherRolesPipe.transform('/trainees');
    }

    reload
      ? this.reload(`/app/program-users${rolePostfix}`)
      : this.router.navigateByUrl(`/app/program-users${rolePostfix}`);
  }

  public programUsersListActive(): boolean {
    return this.router.url.includes('/app/program-users');
  }

  public getDashboardUrl(programRoleId: string = this.dashboardBackProgramRoleId): string {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);

    return `/${prefix}/dashboard/${programRoleId}`;
  }

  public dashboard(programRoleId: string = this.dashboardBackProgramRoleId, reload = false): void {
    if (!programRoleId) {
      return;
    }

    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);

    reload
      ? this.reload(`/${prefix}/dashboard/${programRoleId}`)
      : this.router.navigateByUrl(`/${prefix}/dashboard/${programRoleId}`);
  }

  public dashboardForm(programRoleId: string, formId: number, targetProgramRoleId: string, targetUserId: string): void {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);

    this.router.navigate([`/${prefix}/dashboard/${programRoleId}`], {
      queryParams: {
        formId: formId,
        targetProgramRoleId: targetProgramRoleId,
        targetUserId: targetUserId
      }
    });
  }

  public dashboardActive(): boolean {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);
    return this.router.url.includes(`/${prefix}/dashboard`);
  }

  public performanceDashboard(): void {
    this.router.navigateByUrl('/performance-evaluation/performance-dashboard');
  }

  public performanceDashboardActive(): boolean {
    return this.router.url.includes('/performance-evaluation/performance-dashboard');
  }

  public sites(): void {
    this.router.navigateByUrl('/system-administration/sites');
  }

  public sitesActive(): boolean {
    return this.router.url.includes('/system-administration/sites');
  }

  public users(): void {
    this.router.navigateByUrl('/site-administration/users');
  }

  public usersActive(): boolean {
    return this.router.url === '/site-administration/users';
  }

  public programs(): void {
    this.router.navigateByUrl('/site-administration/programs');
  }

  public programsActive(): boolean {
    return this.router.url === '/site-administration/programs';
  }

  public siteLists(): void {
    this.router.navigateByUrl('/site-administration/site-lists');
  }

  public siteListsActive(): boolean {
    return this.router.url.startsWith('/site-administration/site-lists');
  }

  public addOns(): void {
    this.router.navigateByUrl('/site-administration/add-ons');
  }

  public addOnsActive(): boolean {
    return this.router.url.startsWith('/site-administration/add-ons');
  }

  public activeDirectoryAddOn(): void {
    this.router.navigateByUrl('/site-administration/add-ons/active-directory');
  }

  public powerDmsAddOn(): void {
    this.router.navigateByUrl('/site-administration/add-ons/power-dms');
  }

  public cadIntegrationAddOn(): void {
    this.router.navigateByUrl('/site-administration/add-ons/cad-integration');
  }

  public formInstanceActive(): boolean {
    const prefix = CustomUtils.getProgramTypeStringUrl(this.tokenStorage.userInfo.CurrentProgram.Type);
    return this.router.url.includes(`/${prefix}/form-instance`);
  }

  multipleTraineeFormInstancesActive(): boolean {
    return this.router.url.includes(`/multiple/form-instance`);
  }

  public quizInstanceActive(): boolean {
    return this.router.url.includes('/app/quiz-instance');
  }

  public isScheduleByTrainer(): boolean {
    return this.router.url.includes('/field-training/schedule/by-trainer');
  }

  public isScheduleByTrainee(): boolean {
    return this.router.url.includes('/field-training/schedule/by-trainee');
  }

  public parentSchedule(): void {
    this.router.navigateByUrl('/field-training/schedule');
  }

  public parentScheduleActive(): boolean {
    return this.router.url.includes('/field-training/schedule');
  }

  public quizInstance(quizInstanceId: number): void {
    this.router.navigateByUrl(`/app/quiz-instance/${quizInstanceId}/questions`);
  }

  public quizInstanceNewTab(quizInstanceId: number): void {
    this.openInNewTab(`/app/quiz-instance/${quizInstanceId}/questions`);
  }

  public formInstance(formInstanceId: number, IsForMultipleTrainee = false): Promise<boolean> {
    if (IsForMultipleTrainee) {
      const url = `/${getProgramTypeStringUrl(
        this.tokenStorage.userInfo.CurrentProgram.Type
      )}/multiple/form-instance/${formInstanceId}`;
      return this.router.navigateByUrl(url);
    }
    const url = `/${getProgramTypeStringUrl(
      this.tokenStorage.userInfo.CurrentProgram.Type
    )}/form-instance/${formInstanceId}`;

    return this.router.navigateByUrl(url);
  }

  public formInstanceNewTab(formInstanceId: number) {
    const url = `/${getProgramTypeStringUrl(
      this.tokenStorage.userInfo.CurrentProgram.Type
    )}/form-instance/${formInstanceId}`;

    this.openInNewTab(url);
  }

  public downloadResource(id: string): void {
    this.router.navigateByUrl(`/app/resource-download/${id}`);
  }

  public downloadExport(id: string): void {
    this.router.navigateByUrl(`/app/export-download/${id}`);
  }

  public clearQueryParams(): void {
    this.router.navigateByUrl(location.pathname);
  }

  public forbidden(): void {
    this.router.navigateByUrl('/app/forbidden');
  }

  public blank(): void {
    this.router.navigateByUrl('/blank');
  }

  public activityListActive(): boolean {
    return this.router.url.includes('/activities-collecting');
  }

  public mentoringMatching(programRoleId: string): void {
    this.router.navigateByUrl(`/mentoring/mentoring-matching/${programRoleId}`);
  }

  public systemDashboard(): void {
    this.router.navigateByUrl('/system-administration/dashboard');
  }

  public systemDashboardActive(): boolean {
    return this.router.url.includes('/system-administration/dashboard');
  }

  public siteDashboard(): void {
    this.router.navigateByUrl('/site-administration/dashboard');
  }

  public siteDashboardActive(): boolean {
    return this.router.url.includes('/site-administration/dashboard');
  }

  public trainingCoursesActive(): boolean {
    return this.router.url.includes('/training-courses');
  }

  public trainingCoursesDashboardActive(): boolean {
    return this.router.url.includes('/training-courses/dashboard');
  }

  public trainingCoursesDashboard(): void {
    this.router.navigateByUrl('/training-courses/dashboard');
  }

  public trainingCoursesReportsActive(): boolean {
    return (
      this.router.url.includes('/training-courses/training-reports') ||
      this.router.url.includes('/training-courses/training-hours')
    );
  }

  public trainingCoursesReports(): void {
    this.router.navigateByUrl('/training-courses/training-reports');
  }

  public trainingCourses(): void {
    this.router.navigateByUrl('/training-courses');
  }

  public trainingCourse(id: number): void {
    this.router.navigateByUrl(`/training-courses/${id}`);
  }

  public trainingCourseSchedule(id: number): void {
    this.router.navigateByUrl(`/training-courses/${id}/schedule`);
  }

  public programTrainingCourses(): void {
    this.router.navigateByUrl('/program-setup/training-courses');
  }

  public programTrainingCourse(id: number): void {
    this.router.navigateByUrl(`/program-setup/training-course/${id}`);
  }

  public programTrainingCoursesActive(): boolean {
    return this.router.url.includes('/program-setup/training-courses');
  }

  public trainingCourseClass(id: number): void {
    this.router.navigateByUrl(`/training-courses/classes/${id}`);
  }

  public trainingCourseRequest(id: number): void {
    this.router.navigateByUrl(`/training-courses/requests/${id}`);
  }

  public newTrainingCourseRequest(trainingCourseClassId: number): void {
    this.router.navigateByUrl(`/training-courses/requests/0?trainingCourseClassId=${trainingCourseClassId}`);
  }

  public openInNewTab(link: string) {
    this.urlSecurityService.safeOpenExternal(link);
  }

  private findLastActivatedRouteAndTitle(route: ActivatedRoute): [ActivatedRoute, string] {
    let child = route.firstChild;
    let lastRoute = child;
    let stateName = '';

    while (child != null) {
      if (child.snapshot.data['stateName'] !== undefined) {
        stateName += child.snapshot.data['stateName'] + APP_CONSTANTS.TITLE_SEPARATOR;
      }
      lastRoute = child;
      child = child.firstChild;
    }
    const lastIndex = stateName.lastIndexOf(APP_CONSTANTS.TITLE_SEPARATOR);
    stateName = stateName.substring(0, lastIndex);
    return [lastRoute, stateName];
  }
}
